import type { z } from 'zod';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/cdr/api-schema';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { cdrApi } from '@/services';
import { encounterTypeSchema } from '@/types/encounter-type-schema';
import { queryEncounterResSchema } from '~/generated/cdr/zod-schema';

const API_PATH = '/v1/encounter/queryEncounter/{id}' as const;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const encounterByIdSchema = zodRemoveNullable(queryEncounterResSchema, {
  includeMask: {
    id: true,
    visitStartDatetime: true,
  },
}).extend({
  encounterType: encounterTypeSchema,
});

export type QueryEncounterByIdData = z.infer<typeof encounterByIdSchema>;

const queryFn = async (encounterId: string, signal: AbortSignal) => {
  const { data: response } = await cdrApi.get<APIResponse>(
    API_PATH.replace('{id}', encounterId),
    { signal },
  );

  return encounterByIdSchema.parse(response.data);
};

export const useQueryEncounterById = (
  encounterId?: string,
  options?: QueryOptions<QueryEncounterByIdData>,
) => {
  return useQuery({
    queryKey: [API_PATH, encounterId],
    queryFn: encounterId
      ? ({ signal }) => queryFn(encounterId, signal)
      : skipToken,
    ...options,
  });
};
